import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  FlatList,
  Dimensions,
  Alert,
  Modal,
  TextInput,
  RefreshControl,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Image,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    const bid = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
    if (bid) return bid;
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) { params.set('company_id', companyId); params.set('company_id', companyId); }
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' };
    if (token) headers.Authorization = 'Bearer ' + token;
    const fetchOpts = { headers, ...options };
    if (options.body) fetchOpts.body = JSON.stringify(options.body);
    const response = await fetch(url, fetchOpts);

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width } = Dimensions.get('window');

const TestResultsScreen = ({ navigation, route }) => {
  
  const jobId = route?.params?.jobId || route?.params?.job_id || null;
  const jobTitle = route?.params?.jobTitle || '';
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedResults, setSelectedResults] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'All',
    testType: 'All',
    score: 'All',
    position: 'All'
  });

  const [resultsData, setResultsData] = useState({
    overview: {
      totalResults: 156,
      averageScore: 76.8,
      passRate: 68.5,
      completionRate: 82.5,
      highestScore: 98,
      lowestScore: 42,
      totalTime: 6840,
      averageTime: 43.8,
      topPerformers: 23,
      needsReview: 18,
      flaggedResults: 8
    },
    results: [],
    tests: [],
    analytics: {
      scoreDistribution: [
        { range: '90-100', count: 23, percentage: 14.7 },
        { range: '80-89', count: 45, percentage: 28.8 },
        { range: '70-79', count: 52, percentage: 33.3 },
        { range: '60-69', count: 28, percentage: 17.9 },
        { range: '50-59', count: 8, percentage: 5.1 }
      ],
      testTypePerformance: [
        { type: 'JavaScript', avgScore: 78.5, count: 45, passRate: 71.1 },
        { type: 'React', avgScore: 74.3, count: 38, passRate: 65.8 },
        { type: 'Python', avgScore: 81.2, count: 32, passRate: 78.1 },
        { type: 'SQL', avgScore: 69.7, count: 25, passRate: 60.0 },
        { type: 'Problem Solving', avgScore: 72.9, count: 16, passRate: 62.5 }
      ],
      positionPerformance: [
        { position: 'Frontend Dev', avgScore: 79.2, count: 45, passRate: 73.3 },
        { position: 'Backend Dev', avgScore: 82.1, count: 38, passRate: 78.9 },
        { position: 'Full Stack', avgScore: 76.8, count: 52, passRate: 67.3 },
        { position: 'Data Scientist', avgScore: 84.3, count: 21, passRate: 81.0 }
      ],
      timeAnalysis: [
        { range: '0-30 min', count: 34, avgScore: 72.1 },
        { range: '30-45 min', count: 67, avgScore: 78.3 },
        { range: '45-60 min', count: 42, avgScore: 76.9 },
        { range: '60+ min', count: 13, avgScore: 69.2 }
      ],
      weeklyTrends: [
        { week: 'Week 1', completed: 23, avgScore: 75.2 },
        { week: 'Week 2', completed: 31, avgScore: 77.8 },
        { week: 'Week 3', completed: 28, avgScore: 76.1 },
        { week: 'Week 4', completed: 35, avgScore: 78.4 },
        { week: 'Week 5', completed: 25, avgScore: 74.9 },
        { week: 'Week 6', completed: 14, avgScore: 79.1 }
      ]
    },
    notifications: [
      {
        id: 1,
        type: 'urgent',
        title: 'Low Performance Alert',
        message: '8 candidates scored below 50%',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        read: false
      },
      {
        id: 2,
        type: 'info',
        title: 'High Scorer Alert',
        message: '5 candidates achieved 95%+ scores',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        read: false
      },
      {
        id: 3,
        type: 'success',
        title: 'Analysis Complete',
        message: 'Weekly performance analysis is ready',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
        read: true
      }
    ]
  });

  const statuses = ['All', 'Passed', 'Failed', 'Under Review', 'Flagged', 'Retake Required'];
  const testTypes = ['All', 'JavaScript', 'React', 'Python', 'SQL', 'Problem Solving', 'System Design', 'Algorithms'];
  const scoreRanges = ['All', '90-100', '80-89', '70-79', '60-69', '50-59', 'Below 50'];
  const positions = ['All', 'Frontend Developer', 'Backend Developer', 'Full Stack Developer', 'Data Scientist', 'DevOps Engineer'];

  const tabs = [
    { id: 'overview', title: 'Overview', icon: 'view-dashboard' },
    { id: 'results', title: 'Results', icon: 'chart-line' },
    { id: 'analysis', title: 'Analysis', icon: 'chart-donut' },
    { id: 'insights', title: 'Insights', icon: 'lightbulb' },
    { id: 'reports', title: 'Reports', icon: 'file-document' },
  ];

    // Company logo (same as EditSupplierScreen)
  useFocusEffect(React.useCallback(() => { loadCompanyLogo(); }, []));
  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) setCompanyName(profile.name || profile.company_name);
      }
    } catch (e) { console.warn('Logo load error:', e.message); }
  };

useEffect(() => {
    loadResultsData();
    const interval = setInterval(loadResultsData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadResultsData = async () => {
    if (!refreshing) setLoading(true);
    
    try {
      // Try API first, then fall back to cache
      let storedData = null;
      try {
        const response = await apiRequest('test-results');
        if (response.success && response.data && (Array.isArray(response.data) ? response.data.length > 0 : Object.keys(response.data).length > 0)) {
          storedData = JSON.stringify(response.data);
          await AsyncStorage.setItem('testResultsData', storedData);
        }
      } catch (apiError) {
        console.warn('test-results API unavailable:', apiError.message);
      }
      // Fallback: fetch from interviews + applications tables
      if (!storedData) {
        try {
          const [intRes, appRes] = await Promise.all([
            apiRequest('interviews').catch(() => ({ success: false })),
            apiRequest('applications').catch(() => ({ success: false })),
          ]);
          if (intRes.success && intRes.data && intRes.data.length > 0) {
            const apps = (appRes.success && appRes.data) ? appRes.data : [];
            const results = intRes.data.map(iv => {
              const app = apps.find(a => String(a.id) === String(iv.job_application_id));
              return {
                id: iv.id, candidateName: app?.candidate_name || iv.candidate_name || 'Unknown',
                candidateEmail: app?.candidate_email || '', position: app?.current_title || '',
                testType: 'Technical Assessment', testName: (app?.current_title || 'Technical') + ' Assessment',
                score: iv.rating ? iv.rating * 20 : (app?.ai_score || null),
                status: iv.status === 'completed' ? 'Passed' : 'In Progress',
                completedDate: iv.interview_date, timeSpent: 45,
              };
            });
            const mockData = await generateMockResultsData();
            mockData.results = results;
            storedData = JSON.stringify(mockData);
            await AsyncStorage.setItem('testResultsData', storedData);
          }
        } catch (e2) { console.warn('Interviews fallback failed:', e2.message); }
      }
      if (!storedData) {
        storedData = await AsyncStorage.getItem('testResultsData');
      }
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setResultsData(prev => ({ ...prev, ...parsedData }));
      } else {
        const mockData = await generateMockResultsData();
        setResultsData(prev => ({ ...prev, ...mockData }));
        await AsyncStorage.setItem('testResultsData', JSON.stringify(mockData));
        try { await apiRequest('test-results', { method: 'POST', body: mockData }); } catch(e) {}
      }
    } catch (error) {
      console.error('Error loading results data:', error);
      Alert.alert('Error', 'Failed to load results data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const generateMockResultsData = async () => {
    const results = Array.from({ length: 156 }, (_, index) => ({
      id: index + 1,
      candidateName: `Candidate ${index + 1}`,
      candidateEmail: `candidate${index + 1}@email.com`,
      position: positions[Math.floor(Math.random() * (positions.length - 1)) + 1],
      testType: testTypes[Math.floor(Math.random() * (testTypes.length - 1)) + 1],
      testName: `${testTypes[Math.floor(Math.random() * (testTypes.length - 1)) + 1]} Assessment`,
      score: Math.floor(Math.random() * 58) + 42,
      maxScore: 100,
      percentage: null,
      passed: null,
      timeSpent: Math.floor(Math.random() * 90) + 15,
      questionsTotal: Math.floor(Math.random() * 20) + 10,
      questionsCorrect: null,
      questionsIncorrect: null,
      questionsSkipped: Math.floor(Math.random() * 3),
      completedDate: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
      startedDate: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
      attempt: Math.floor(Math.random() * 3) + 1,
      status: null,
      feedback: 'Good performance with areas for improvement.',
      strengths: ['Problem solving', 'Code quality', 'Logic'],
      weaknesses: ['Time management', 'Edge cases', 'Optimization'],
      recommendation: null,
      flagged: Math.random() > 0.95,
      reviewNotes: 'Candidate showed strong understanding of core concepts.',
      sectionScores: {
        'Syntax & Basics': Math.floor(Math.random() * 40) + 60,
        'Problem Solving': Math.floor(Math.random() * 40) + 60,
        'Best Practices': Math.floor(Math.random() * 40) + 60,
        'Advanced Concepts': Math.floor(Math.random() * 40) + 60
      },
      difficulty: ['Easy', 'Medium', 'Hard'][Math.floor(Math.random() * 3)],
      passingScore: 70,
      retakeAllowed: Math.random() > 0.7,
      cheatingDetected: Math.random() > 0.98,
      proctored: Math.random() > 0.3
    }));

    // Calculate derived fields
    results.forEach(result => {
      result.percentage = Math.round((result.score / result.maxScore) * 100);
      result.passed = result.score >= result.passingScore;
      result.questionsCorrect = Math.floor((result.score / 100) * result.questionsTotal);
      result.questionsIncorrect = result.questionsTotal - result.questionsCorrect - result.questionsSkipped;
      
      if (result.flagged || result.cheatingDetected) {
        result.status = 'Flagged';
      } else if (result.score < 50) {
        result.status = 'Under Review';
      } else if (result.passed) {
        result.status = 'Passed';
      } else {
        result.status = result.retakeAllowed ? 'Retake Required' : 'Failed';
      }

      if (result.score >= 90) {
        result.recommendation = 'Highly Recommended';
      } else if (result.score >= 75) {
        result.recommendation = 'Recommended';
      } else if (result.score >= 60) {
        result.recommendation = 'Consider with Caution';
      } else {
        result.recommendation = 'Not Recommended';
      }
    });

    const tests = Array.from({ length: 25 }, (_, index) => ({
      id: index + 1,
      name: `${testTypes[Math.floor(Math.random() * (testTypes.length - 1)) + 1]} Assessment ${index + 1}`,
      type: testTypes[Math.floor(Math.random() * (testTypes.length - 1)) + 1],
      totalResults: Math.floor(Math.random() * 20) + 5,
      averageScore: Math.floor(Math.random() * 40) + 60,
      passRate: Math.floor(Math.random() * 40) + 50,
      createdDate: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
      status: ['Active', 'Completed', 'Draft'][Math.floor(Math.random() * 3)],
      duration: Math.floor(Math.random() * 60) + 30,
      questions: Math.floor(Math.random() * 20) + 10,
      difficulty: ['Easy', 'Medium', 'Hard'][Math.floor(Math.random() * 3)]
    }));

    return {
      results,
      tests
    };
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadResultsData();
  };

  const saveResultsData = async (updatedData) => {
    try {
      try { await apiRequest('test-results', { method: 'PUT', body: updatedData }); } catch(e) { console.warn('API save failed:', e.message); }
      await AsyncStorage.setItem('testResultsData', JSON.stringify(updatedData));
      setResultsData(prev => ({ ...prev, ...updatedData }));
    } catch (error) {
      console.error('Error saving results data:', error);
      Alert.alert('Error', 'Failed to save results data');
    }
  };

  const updateResultStatus = async (resultId, newStatus) => {
    try {
      const updatedResults = resultsData.results.map(result =>
        result.id === resultId ? { ...result, status: newStatus } : result
      );

      const updatedData = {
        results: updatedResults
      };

      await saveResultsData(updatedData);
      Alert.alert('Success', `Result status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating result status:', error);
      Alert.alert('Error', 'Failed to update result status');
    }
  };

  const getFilteredResults = () => {
    return resultsData.results.filter(result => {
      const matchesSearch = String(result.candidateName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                           String(result.candidateEmail ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                           String(result.testType ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = filters.status === 'All' || result.status === filters.status;
      const matchesTestType = filters.testType === 'All' || result.testType === filters.testType;
      const matchesPosition = filters.position === 'All' || result.position === filters.position;
      const matchesScore = filters.score === 'All' || 
        (filters.score === '90-100' && result.score >= 90) ||
        (filters.score === '80-89' && result.score >= 80 && result.score < 90) ||
        (filters.score === '70-79' && result.score >= 70 && result.score < 80) ||
        (filters.score === '60-69' && result.score >= 60 && result.score < 70) ||
        (filters.score === '50-59' && result.score >= 50 && result.score < 60) ||
        (filters.score === 'Below 50' && result.score < 50);
      
      return matchesSearch && matchesStatus && matchesTestType && matchesPosition && matchesScore;
    });
  };

  const renderStatCard = (title, value, icon, color, subtitle = null, trend = null, onPress = null) => (
    <TouchableOpacity
      style={[styles.statCard, { borderLeftColor: color }]}
      onPress={onPress}
      disabled={!onPress}
    >
      <View style={styles.statContent}>
        <View style={styles.statHeader}>
          <Text style={styles.statValue}>{value}</Text>
          {trend && (
            <View style={[styles.trendIndicator, { backgroundColor: trend > 0 ? '#10b981' : '#dc2626' }]}>
              <MaterialCommunityIcons 
                name={trend > 0 ? "trending-up" : "trending-down"} 
                size={12} 
                color="#fff" 
              />
              <Text style={styles.trendText}>{Math.abs(trend)}%</Text>
            </View>
          )}
        </View>
        <Text style={styles.statTitle}>{title}</Text>
        {subtitle && <Text style={styles.statSubtitle}>{subtitle}</Text>}
      </View>
      <MaterialCommunityIcons name={icon} size={32} color={color} />
    </TouchableOpacity>
  );

  const renderResult = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.resultCard,
        selectedResults.includes(item.id) && styles.selectedCard
      ]}
      onPress={() => {
        setSelectedResult(item);
        setModalType('result_details');
        setShowModal(true);
      }}
      onLongPress={() => {
        if (selectedResults.includes(item.id)) {
          setSelectedResults(prev => prev.filter(id => id !== item.id));
        } else {
          setSelectedResults(prev => [...prev, item.id]);
        }
      }}
    >
      <View style={styles.resultHeader}>
        <View style={styles.resultInfo}>
          <Text style={styles.resultName}>{item.candidateName}</Text>
          <Text style={styles.resultTest}>{item.testName}</Text>
          <Text style={styles.resultEmail}>{item.candidateEmail}</Text>
          <Text style={styles.resultPosition}>{item.position}</Text>
        </View>
        <View style={styles.resultStatus}>
          <View style={[
            styles.statusBadge,
            { backgroundColor: 
              item.status === 'Passed' ? '#10b981' : 
              item.status === 'Failed' ? '#dc2626' :
              item.status === 'Under Review' ? '#f59e0b' :
              item.status === 'Flagged' ? '#dc2626' :
              item.status === 'Retake Required' ? '#8b5cf6' : '#64748b'
            }
          ]}>
            <Text style={styles.statusText}>{item.status}</Text>
          </View>
          <Text style={[
            styles.scoreText,
            { color: 
              item.score >= 90 ? '#10b981' :
              item.score >= 70 ? '#f59e0b' : '#dc2626'
            }
          ]}>
            {item.score}%
          </Text>
          {selectedResults.includes(item.id) && (
            <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
          )}
        </View>
      </View>
      
      <View style={styles.resultDetails}>
        <View style={styles.resultDetailItem}>
          <Text style={styles.resultDetailLabel}>Time Spent:</Text>
          <Text style={styles.resultDetailValue}>{item.timeSpent} min</Text>
        </View>
        <View style={styles.resultDetailItem}>
          <Text style={styles.resultDetailLabel}>Questions:</Text>
          <Text style={styles.resultDetailValue}>{item.questionsCorrect}/{item.questionsTotal}</Text>
        </View>
        <View style={styles.resultDetailItem}>
          <Text style={styles.resultDetailLabel}>Attempt:</Text>
          <Text style={styles.resultDetailValue}>{item.attempt}</Text>
        </View>
        <View style={styles.resultDetailItem}>
          <Text style={styles.resultDetailLabel}>Completed:</Text>
          <Text style={styles.resultDetailValue}>{item.completedDate}</Text>
        </View>
      </View>

      {item.flagged && (
        <View style={styles.flaggedIndicator}>
          <MaterialCommunityIcons name="flag" size={16} color="#dc2626" />
          <Text style={styles.flaggedText}>Flagged for Review</Text>
        </View>
      )}
    </TouchableOpacity>
  );

  const renderAnalyticsChart = (title, data, type = 'bar') => (
    <View style={styles.chartContainer}>
      <Text style={styles.chartTitle}>{title}</Text>
      <View style={styles.chartContent}>
        {type === 'bar' && data.map((item, index) => (
          <View key={index} style={styles.barChartItem}>
            <View style={styles.barChartBar}>
              <View 
                style={[
                  styles.barChartFill, 
                  { 
                    height: `${(item.avgScore || item.count || item.completed || item.passRate || 0) / (type === 'score' ? 100 : type === 'passRate' ? 100 : Math.max(...data.map(d => d.avgScore || d.count || d.completed || d.passRate))) * 100}%`,
                    backgroundColor: '#0e4e96'
                  }
                ]} 
              />
            </View>
            <Text style={styles.barChartLabel}>{item.type || item.range || item.position || item.week}</Text>
            <Text style={styles.barChartValue}>
              {item.avgScore ? `${item.avgScore}%` : item.count || item.completed || `${item.passRate}%`}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );

  const renderOverviewTab = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Key Metrics */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Results Overview</Text>
        <View style={styles.statsGrid}>
          {renderStatCard(
            'Total Results',
            resultsData.overview.totalResults,
            'chart-line',
            '#0e4e96',
            'All test results',
            8.2,
            () => setActiveTab('results')
          )}
          {renderStatCard(
            'Average Score',
            `${resultsData.overview.averageScore}%`,
            'star',
            '#f59e0b',
            'Overall performance',
            3.1
          )}
          {renderStatCard(
            'Pass Rate',
            `${resultsData.overview.passRate}%`,
            'check-circle',
            '#10b981',
            'Success rate',
            5.7
          )}
          {renderStatCard(
            'Completion Rate',
            `${resultsData.overview.completionRate}%`,
            'clock-check',
            '#06b6d4',
            'Finished tests',
            2.3
          )}
          {renderStatCard(
            'Top Performers',
            resultsData.overview.topPerformers,
            'trophy',
            '#8b5cf6',
            '90%+ scores',
            12.5
          )}
          {renderStatCard(
            'Needs Review',
            resultsData.overview.needsReview,
            'alert-circle',
            '#dc2626',
            'Flagged results',
            -8.1
          )}
        </View>
      </View>

      {/* Quick Actions */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <View style={styles.quickActionsGrid}>
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => setActiveTab('results')}
          >
            <MaterialCommunityIcons name="chart-line" size={24} color="#10b981" />
            <Text style={styles.quickActionText}>View All Results</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => setActiveTab('analysis')}
          >
            <MaterialCommunityIcons name="chart-donut" size={24} color="#8b5cf6" />
            <Text style={styles.quickActionText}>Detailed Analysis</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => setActiveTab('insights')}
          >
            <MaterialCommunityIcons name="lightbulb" size={24} color="#f59e0b" />
            <Text style={styles.quickActionText}>Get Insights</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => Alert.alert('Export', 'Export functionality would be implemented here')}
          >
            <MaterialCommunityIcons name="download" size={24} color="#06b6d4" />
            <Text style={styles.quickActionText}>Export Results</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Score Distribution */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Score Distribution</Text>
        {renderAnalyticsChart('Score Range Distribution', resultsData.analytics.scoreDistribution)}
      </View>

      {/* Performance Metrics */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance Metrics</Text>
        <View style={styles.additionalMetrics}>
          <View style={styles.metricItem}>
            <Text style={styles.metricLabel}>Highest Score</Text>
            <Text style={styles.metricValue}>{resultsData.overview.highestScore}%</Text>
          </View>
          <View style={styles.metricItem}>
            <Text style={styles.metricLabel}>Lowest Score</Text>
            <Text style={styles.metricValue}>{resultsData.overview.lowestScore}%</Text>
          </View>
          <View style={styles.metricItem}>
            <Text style={styles.metricLabel}>Average Time</Text>
            <Text style={styles.metricValue}>{resultsData.overview.averageTime} min</Text>
          </View>
          <View style={styles.metricItem}>
            <Text style={styles.metricLabel}>Flagged Results</Text>
            <Text style={styles.metricValue}>{resultsData.overview.flaggedResults}</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  const renderResultsTab = () => {
    const filteredResults = getFilteredResults();
    
    return (
      <View style={styles.tabContentFull}>
        {/* Search and Filters */}
        <View style={styles.searchSection}>
          <View style={styles.searchContainer}>
            <MaterialCommunityIcons name="magnify" size={20} color="#64748b" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search results..."
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>
          <TouchableOpacity
            style={styles.filterButton}
            onPress={() => setShowFilters(!showFilters)}
          >
            <MaterialCommunityIcons name="filter" size={20} color="#0e4e96" />
          </TouchableOpacity>
        </View>

        {/* Bulk Actions */}
        {selectedResults.length > 0 && (
          <View style={styles.bulkActionsContainer}>
            <Text style={styles.bulkActionsText}>{selectedResults.length} selected</Text>
            <View style={styles.bulkActions}>
              <TouchableOpacity
                style={[styles.bulkActionButton, { backgroundColor: '#f59e0b' }]}
                onPress={() => Alert.alert('Bulk Review', 'Bulk review functionality would be implemented here')}
              >
                <Text style={styles.bulkActionText}>Review</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.bulkActionButton, { backgroundColor: '#06b6d4' }]}
                onPress={() => Alert.alert('Export Selected', 'Export selected results functionality would be implemented here')}
              >
                <Text style={styles.bulkActionText}>Export</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.bulkActionButton, { backgroundColor: '#64748b' }]}
                onPress={() => setSelectedResults([])}
              >
                <Text style={styles.bulkActionText}>Clear</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Results Stats */}
        <View style={styles.itemStats}>
          <Text style={styles.itemStatsText}>
            Showing {filteredResults.length} of {resultsData.results.length} results
          </Text>
        </View>
        
        {/* Results List */}
        <FlatList
          data={filteredResults}
          renderItem={renderResult}
          keyExtractor={(item) => item.id.toString()}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.itemsList}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <MaterialCommunityIcons name="chart-line" size={64} color="#94a3b8" />
              <Text style={styles.emptyText}>No results found</Text>
              <Text style={styles.emptySubtext}>Try adjusting your search or filters</Text>
            </View>
          }
        />
      </View>
    );
  };

  const renderAnalysisTab = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Test Type Performance */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance by Test Type</Text>
        {renderAnalyticsChart('Average Scores by Test Type', resultsData.analytics.testTypePerformance, 'score')}
      </View>

      {/* Position Performance */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance by Position</Text>
        {renderAnalyticsChart('Average Scores by Position', resultsData.analytics.positionPerformance, 'score')}
      </View>

      {/* Time Analysis */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Time vs Performance Analysis</Text>
        {renderAnalyticsChart('Performance by Time Spent', resultsData.analytics.timeAnalysis, 'score')}
      </View>

      {/* Weekly Trends */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Weekly Performance Trends</Text>
        {renderAnalyticsChart('Weekly Test Completion and Scores', resultsData.analytics.weeklyTrends, 'completed')}
      </View>
    </ScrollView>
  );

  const renderInsightsTab = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Insights</Text>
        
        <View style={styles.insightCard}>
          <MaterialCommunityIcons name="trending-up" size={24} color="#10b981" />
          <View style={styles.insightContent}>
            <Text style={styles.insightTitle}>Strong Performance Trend</Text>
            <Text style={styles.insightDescription}>
              Average scores have increased by 5.7% over the past month, indicating improved candidate quality.
            </Text>
          </View>
        </View>

        <View style={styles.insightCard}>
          <MaterialCommunityIcons name="clock-alert" size={24} color="#f59e0b" />
          <View style={styles.insightContent}>
            <Text style={styles.insightTitle}>Optimal Test Duration</Text>
            <Text style={styles.insightDescription}>
              Candidates who spend 30-45 minutes achieve the highest average scores (78.3%).
            </Text>
          </View>
        </View>

        <View style={styles.insightCard}>
          <MaterialCommunityIcons name="star" size={24} color="#8b5cf6" />
          <View style={styles.insightContent}>
            <Text style={styles.insightTitle}>Top Performing Test Type</Text>
            <Text style={styles.insightDescription}>
              Python tests show the highest average scores (81.2%) and pass rates (78.1%).
            </Text>
          </View>
        </View>

        <View style={styles.insightCard}>
          <MaterialCommunityIcons name="account-group" size={24} color="#06b6d4" />
          <View style={styles.insightContent}>
            <Text style={styles.insightTitle}>Position-Based Performance</Text>
            <Text style={styles.insightDescription}>
              Backend developers consistently outperform other positions with 82.1% average scores.
            </Text>
          </View>
        </View>

        <View style={styles.insightCard}>
          <MaterialCommunityIcons name="alert-circle" size={24} color="#dc2626" />
          <View style={styles.insightContent}>
            <Text style={styles.insightTitle}>Areas for Improvement</Text>
            <Text style={styles.insightDescription}>
              SQL tests have the lowest pass rate (60.0%) - consider reviewing test difficulty or providing additional resources.
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recommendations</Text>
        
        <View style={styles.recommendationCard}>
          <Text style={styles.recommendationTitle}>🎯 Optimize Test Timing</Text>
          <Text style={styles.recommendationText}>
            Set recommended test duration to 45 minutes for optimal performance outcomes.
          </Text>
        </View>

        <View style={styles.recommendationCard}>
          <Text style={styles.recommendationTitle}>📚 Improve SQL Resources</Text>
          <Text style={styles.recommendationText}>
            Provide additional SQL training materials or adjust test difficulty to improve pass rates.
          </Text>
        </View>

        <View style={styles.recommendationCard}>
          <Text style={styles.recommendationTitle}>🔍 Focus on Quality</Text>
          <Text style={styles.recommendationText}>
            Continue current screening process as it's attracting higher-quality candidates.
          </Text>
        </View>
      </View>
    </ScrollView>
  );

  const renderReportsTab = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Available Reports</Text>
        
        <TouchableOpacity style={styles.reportCard}>
          <MaterialCommunityIcons name="file-chart" size={24} color="#0e4e96" />
          <View style={styles.reportContent}>
            <Text style={styles.reportTitle}>Comprehensive Performance Report</Text>
            <Text style={styles.reportDescription}>
              Detailed analysis of all test results with insights and recommendations
            </Text>
          </View>
          <MaterialCommunityIcons name="download" size={20} color="#64748b" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.reportCard}>
          <MaterialCommunityIcons name="chart-line" size={24} color="#10b981" />
          <View style={styles.reportContent}>
            <Text style={styles.reportTitle}>Trend Analysis Report</Text>
            <Text style={styles.reportDescription}>
              Weekly and monthly performance trends with comparative analysis
            </Text>
          </View>
          <MaterialCommunityIcons name="download" size={20} color="#64748b" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.reportCard}>
          <MaterialCommunityIcons name="account-group" size={24} color="#8b5cf6" />
          <View style={styles.reportContent}>
            <Text style={styles.reportTitle}>Candidate Summary Report</Text>
            <Text style={styles.reportDescription}>
              Individual candidate performance summaries and recommendations
            </Text>
          </View>
          <MaterialCommunityIcons name="download" size={20} color="#64748b" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.reportCard}>
          <MaterialCommunityIcons name="clipboard-text" size={24} color="#f59e0b" />
          <View style={styles.reportContent}>
            <Text style={styles.reportTitle}>Test Effectiveness Report</Text>
            <Text style={styles.reportDescription}>
              Analysis of test types, difficulty levels, and effectiveness metrics
            </Text>
          </View>
          <MaterialCommunityIcons name="download" size={20} color="#64748b" />
        </TouchableOpacity>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Custom Reports</Text>
        
        <TouchableOpacity 
          style={styles.customReportButton}
          onPress={() => Alert.alert('Custom Report', 'Custom report builder would be implemented here')}
        >
          <MaterialCommunityIcons name="plus-circle" size={24} color="#fff" />
          <Text style={styles.customReportText}>Create Custom Report</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );

  const renderTabContent = () => {
    if (loading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading results data...</Text>
        </View>
      );
    }

    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'results':
        return renderResultsTab();
      case 'analysis':
        return renderAnalysisTab();
      case 'insights':
        return renderInsightsTab();
      case 'reports':
        return renderReportsTab();
      default:
        return renderOverviewTab();
    }
  };

  const renderResultDetailsModal = () => (
    <Modal
      visible={showModal && modalType === 'result_details'}
      animationType="slide"
      presentationStyle="pageSheet"
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowModal(false)}>
            <MaterialCommunityIcons name="close" size={24} color="#64748b" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Test Result Details</Text>
          <TouchableOpacity>
            <MaterialCommunityIcons name="share" size={24} color="#0e4e96" />
          </TouchableOpacity>
        </View>
        
        {selectedResult && (
          <ScrollView style={styles.modalContent}>
            <Text style={styles.detailsTitle}>{selectedResult.candidateName}</Text>
            <Text style={styles.detailsSubtitle}>{selectedResult.testName}</Text>
            <Text style={styles.detailsDescription}>{selectedResult.candidateEmail}</Text>
            
            <View style={styles.detailsSection}>
              <Text style={styles.detailsSectionTitle}>Test Performance</Text>
              <View style={styles.detailsItem}>
                <Text style={styles.detailsLabel}>Overall Score:</Text>
                <Text style={[
                  styles.detailsValue,
                  { color: 
                    selectedResult.score >= 90 ? '#10b981' :
                    selectedResult.score >= 70 ? '#f59e0b' : '#dc2626'
                  }
                ]}>
                  {selectedResult.score}%
                </Text>
              </View>
              <View style={styles.detailsItem}>
                <Text style={styles.detailsLabel}>Status:</Text>
                <View style={[
                  styles.statusBadge,
                  { backgroundColor: 
                    selectedResult.status === 'Passed' ? '#10b981' : 
                    selectedResult.status === 'Failed' ? '#dc2626' :
                    selectedResult.status === 'Under Review' ? '#f59e0b' :
                    selectedResult.status === 'Flagged' ? '#dc2626' :
                    selectedResult.status === 'Retake Required' ? '#8b5cf6' : '#64748b'
                  }
                ]}>
                  <Text style={styles.statusText}>{selectedResult.status}</Text>
                </View>
              </View>
              <View style={styles.detailsItem}>
                <Text style={styles.detailsLabel}>Time Spent:</Text>
                <Text style={styles.detailsValue}>{selectedResult.timeSpent} minutes</Text>
              </View>
              <View style={styles.detailsItem}>
                <Text style={styles.detailsLabel}>Questions Correct:</Text>
                <Text style={styles.detailsValue}>{selectedResult.questionsCorrect}/{selectedResult.questionsTotal}</Text>
              </View>
              <View style={styles.detailsItem}>
                <Text style={styles.detailsLabel}>Attempt Number:</Text>
                <Text style={styles.detailsValue}>{selectedResult.attempt}</Text>
              </View>
            </View>

            <View style={styles.detailsSection}>
              <Text style={styles.detailsSectionTitle}>Section Breakdown</Text>
              {Object.entries(selectedResult.sectionScores).map(([section, score]) => (
                <View key={section} style={styles.detailsItem}>
                  <Text style={styles.detailsLabel}>{section}:</Text>
                  <Text style={styles.detailsValue}>{score}%</Text>
                </View>
              ))}
            </View>

            <View style={styles.detailsSection}>
              <Text style={styles.detailsSectionTitle}>Assessment</Text>
              <View style={styles.detailsItem}>
                <Text style={styles.detailsLabel}>Recommendation:</Text>
                <Text style={[
                  styles.detailsValue,
                  { color: 
                    selectedResult.recommendation === 'Highly Recommended' ? '#10b981' :
                    selectedResult.recommendation === 'Recommended' ? '#8b5cf6' :
                    selectedResult.recommendation === 'Consider with Caution' ? '#f59e0b' : '#dc2626'
                  }
                ]}>
                  {selectedResult.recommendation}
                </Text>
              </View>
              <View style={styles.detailsItem}>
                <Text style={styles.detailsLabel}>Feedback:</Text>
                <Text style={styles.detailsValue}>{selectedResult.feedback}</Text>
              </View>
            </View>

            <View style={styles.actionButtons}>
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: '#f59e0b' }]}
                onPress={() => updateResultStatus(selectedResult.id, 'Under Review')}
              >
                <Text style={styles.actionButtonText}>Mark for Review</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: '#10b981' }]}
                onPress={() => updateResultStatus(selectedResult.id, 'Passed')}
              >
                <Text style={styles.actionButtonText}>Approve</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: '#dc2626' }]}
                onPress={() => updateResultStatus(selectedResult.id, 'Flagged')}
              >
                <Text style={styles.actionButtonText}>Flag</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation && navigation.goBack ? navigation.goBack() : null}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />
        </TouchableOpacity>
        {companyLogo ? (
          <Image source={{ uri: companyLogo || undefined }} style={{ width: 30, height: 30, borderRadius: 15, marginRight: 8 }} />
        ) : (
          <MaterialCommunityIcons name="office-building" size={24} color="#fff" style={{ marginRight: 8 }} />
        )}
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>Test Results</Text>
          {jobTitle ? <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>{jobTitle}</Text> : null}
        </View>
        <TouchableOpacity style={styles.notificationButton}>
          <MaterialCommunityIcons name="bell" size={24} color="#fff" />
          {resultsData.notifications.filter(n => !n.read).length > 0 && (
            <View style={styles.notificationBadge}>
              <Text style={styles.notificationBadgeText}>
                {resultsData.notifications.filter(n => !n.read).length}
              </Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      {/* Tabs */}
      
      {/* Cross Navigation */}
      <View style={{ backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 12, paddingVertical: 8 }}>
          {jobTitle ? <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e9615', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, marginRight: 8 }} onPress={() => navigation.goBack()}>
            <MaterialCommunityIcons name="briefcase" size={14} color="#0e4e96" />
            <Text style={{ fontSize: 12, fontWeight: '600', color: '#0e4e96', marginLeft: 4 }}>Job Details</Text>
          </TouchableOpacity> : null}
          <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#8B5CF615', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, marginRight: 8 }} onPress={() => navigation.navigate('TestApplicantsScreen', { jobId, jobTitle, companyLogo, companyName })}>
            <MaterialCommunityIcons name="clipboard-check" size={14} color="#8B5CF6" />
            <Text style={{ fontSize: 12, fontWeight: '600', color: '#8B5CF6', marginLeft: 4 }}>Test Applicants</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#3B82F615', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, marginRight: 8 }} onPress={() => navigation.navigate('TestCandidatesPipelineScreen', { jobId, jobTitle, companyLogo, companyName })}>
            <MaterialCommunityIcons name="account-clock" size={14} color="#3B82F6" />
            <Text style={{ fontSize: 12, fontWeight: '600', color: '#3B82F6', marginLeft: 4 }}>Candidates Pipeline</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F59E0B15', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, marginRight: 8 }} onPress={() => navigation.navigate('TestsBankManagementScreen', { jobId, jobTitle, companyLogo, companyName })}>
            <MaterialCommunityIcons name="database-cog" size={14} color="#F59E0B" />
            <Text style={{ fontSize: 12, fontWeight: '600', color: '#F59E0B', marginLeft: 4 }}>Tests Bank</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
<View style={styles.tabsContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[styles.tab, activeTab === tab.id && styles.activeTab]}
              onPress={() => setActiveTab(tab.id)}
            >
              <MaterialCommunityIcons
                name={tab.icon}
                size={20}
                color={activeTab === tab.id ? '#0e4e96' : '#64748b'}
              />
              <Text style={[styles.tabText, activeTab === tab.id && styles.activeTabText]}>
                {tab.title}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      {renderTabContent()}

      {/* Modals */}
      {renderResultDetailsModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: '#4A90E2',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  notificationButton: {
    padding: 8,
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: '#dc2626',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  tabsContainer: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 4,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  tabContent: {
    flex: 1,
    padding: 20,
  },
  tabContentFull: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748b',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  statCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    width: (width - 50) / 2,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  statContent: {
    flex: 1,
  },
  statHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  trendIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 12,
  },
  trendText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
    marginLeft: 2,
  },
  statTitle: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  statSubtitle: {
    fontSize: 12,
    color: '#94a3b8',
    marginTop: 2,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  quickAction: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    width: (width - 50) / 2,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  quickActionText: {
    marginTop: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1e293b',
    textAlign: 'center',
  },
  additionalMetrics: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  metricItem: {
    width: '48%',
    marginBottom: 16,
  },
  metricLabel: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
    marginBottom: 4,
  },
  metricValue: {
    fontSize: 18,
    color: '#1e293b',
    fontWeight: 'bold',
  },
  searchSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 16,
    color: '#1e293b',
  },
  filterButton: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
  },
  bulkActionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  bulkActionsText: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  bulkActions: {
    flexDirection: 'row',
  },
  bulkActionButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginLeft: 8,
  },
  bulkActionText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  itemStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  itemStatsText: {
    fontSize: 16,
    color: '#64748b',
    fontWeight: '500',
  },
  itemsList: {
    paddingBottom: 20,
  },
  resultCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  selectedCard: {
    borderWidth: 2,
    borderColor: '#10b981',
  },
  resultHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  resultInfo: {
    flex: 1,
  },
  resultName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 2,
  },
  resultTest: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 2,
  },
  resultEmail: {
    fontSize: 12,
    color: '#0e4e96',
    marginBottom: 2,
  },
  resultPosition: {
    fontSize: 12,
    color: '#94a3b8',
  },
  resultStatus: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 8,
  },
  statusText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
  },
  scoreText: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  resultDetails: {
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  resultDetailItem: {
    width: '48%',
    marginBottom: 8,
  },
  resultDetailLabel: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '500',
  },
  resultDetailValue: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '500',
  },
  flaggedIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#fee2e2',
  },
  flaggedText: {
    marginLeft: 8,
    fontSize: 12,
    color: '#dc2626',
    fontWeight: '500',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#64748b',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#94a3b8',
    marginTop: 8,
    textAlign: 'center',
  },
  chartContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 16,
  },
  chartContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: 120,
  },
  barChartItem: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 2,
  },
  barChartBar: {
    width: '80%',
    height: 80,
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    justifyContent: 'flex-end',
    marginBottom: 8,
  },
  barChartFill: {
    width: '100%',
    borderRadius: 4,
    minHeight: 4,
  },
  barChartLabel: {
    fontSize: 10,
    color: '#64748b',
    textAlign: 'center',
    marginBottom: 4,
  },
  barChartValue: {
    fontSize: 10,
    color: '#1e293b',
    fontWeight: '600',
    textAlign: 'center',
  },
  insightCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  insightContent: {
    flex: 1,
    marginLeft: 12,
  },
  insightTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 4,
  },
  insightDescription: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 20,
  },
  recommendationCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#0e4e96',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 8,
  },
  recommendationText: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 20,
  },
  reportCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  reportContent: {
    flex: 1,
    marginLeft: 12,
  },
  reportTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 4,
  },
  reportDescription: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 20,
  },
  customReportButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  customReportText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  detailsTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 4,
  },
  detailsSubtitle: {
    fontSize: 16,
    color: '#64748b',
    marginBottom: 8,
  },
  detailsDescription: {
    fontSize: 14,
    color: '#94a3b8',
    marginBottom: 20,
  },
  detailsSection: {
    marginBottom: 24,
  },
  detailsSectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 12,
  },
  detailsItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  detailsLabel: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  detailsValue: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '500',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginHorizontal: 4,
  },
  actionButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
});

export default TestResultsScreen;